import React, { useEffect, useRef, useState } from 'react';
import { Referral, SRCompleteResponse, SRCompleteResponseAction } from '../services/apiService';
import { Modal } from './Modal';
import { Button } from './Button';
import FeatherIcon from 'feather-icons-react';

export type SRCompleteResponsePayload = SRCompleteResponse;

interface SRCompleteResponseModalProps {
  referral: Referral;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (payload: SRCompleteResponsePayload) => void;
  isLoading?: boolean;
  error?: string | null;
}

const OPTIONS: { action: SRCompleteResponseAction; label: string; hint?: string }[] = [
  { action: 'none', label: 'No response to SR' },
  {
    action: 'acknowledge',
    label: 'Acknowledge referral',
    hint: 'Notify GP that the referral has been processed',
  },
  { action: 'decline', label: 'Decline referral' },
  { action: 'message', label: 'Send custom message' },
];

export const SRCompleteResponseModal: React.FC<SRCompleteResponseModalProps> = ({
  referral,
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  error = null,
}) => {
  const [action, setAction] = useState<SRCompleteResponseAction>('none');
  const [body, setBody] = useState('');
  const errorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setAction('none');
      setBody('');
    }
  }, [isOpen, referral.id]);

  useEffect(() => {
    if (error && !isLoading) {
      errorRef.current?.focus();
    }
  }, [error, isLoading]);

  const showBody =
    action === 'message' || action === 'decline';
  const bodyRequired = action === 'message';
  const bodyLabel =
    action === 'message'
      ? 'Message to GP'
      : 'Optional reason to append';
  const bodyValidationMessage =
    bodyRequired && !body.trim() ? 'Enter a message to continue.' : null;

  const handleConfirm = () => {
    if (bodyRequired && !body.trim()) {
      return;
    }
    onConfirm({
      action,
      body: showBody && body.trim() ? body.trim() : undefined,
    });
  };

  const confirmDisabled =
    isLoading || (bodyRequired && !body.trim());

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isLoading={isLoading}
      preventCloseOnOverlayClick={isLoading}
    >
      <div className="modal-header">
        <h2>Mark Referral as Complete</h2>
        <Button variant="icon" onClick={onClose} title="Close" disabled={isLoading}>
          <FeatherIcon icon="x" size={20} />
        </Button>
      </div>
      <div className="modal-body">
        <p style={{ marginBottom: '8px' }}>
          Mark{' '}
          <strong>
            {referral.patient_first_name} {referral.patient_last_name}
          </strong>{' '}
          as complete (entered into Indici).
        </p>
        <p style={{ fontSize: '13px', color: '#666', marginBottom: '16px' }}>
          Many SR referrals already received an acknowledgment at ingest. Choose whether to
          notify the referring GP via Specialist Referrals.
        </p>

        <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
          <legend style={{ fontWeight: 600, marginBottom: '8px' }}>Response to SR (optional)</legend>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {OPTIONS.map((option) => (
              <label
                key={option.action}
                style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer' }}
              >
                <input
                  type="radio"
                  name="sr-complete-response"
                  value={option.action}
                  checked={action === option.action}
                  disabled={isLoading}
                  onChange={() => setAction(option.action)}
                  style={{ marginTop: '3px' }}
                />
                <span>
                  {option.label}
                  {option.hint && (
                    <span style={{ display: 'block', fontSize: '13px', color: '#666' }}>
                      {option.hint}
                    </span>
                  )}
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        {showBody && (
          <div style={{ marginTop: '16px' }}>
            <label htmlFor="sr-complete-response-body" style={{ fontWeight: 600, fontSize: '14px' }}>
              {bodyLabel}
            </label>
            <textarea
              id="sr-complete-response-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={5}
              className="form-control"
              placeholder={
                action === 'message'
                  ? 'Enter your message to the GP...'
                  : 'Optional additional detail...'
              }
              disabled={isLoading}
              aria-required={bodyRequired}
              aria-invalid={bodyRequired && !body.trim()}
              aria-describedby={
                error
                  ? 'sr-complete-response-error'
                  : bodyValidationMessage
                    ? 'sr-complete-response-validation'
                    : undefined
              }
              style={{ width: '100%', resize: 'vertical', marginTop: '4px' }}
            />
            {bodyValidationMessage && !error && (
              <p
                id="sr-complete-response-validation"
                role="status"
                aria-live="polite"
                className="admin-notice admin-notice--error"
                style={{ marginTop: '8px', marginBottom: 0, padding: '6px 8px' }}
              >
                {bodyValidationMessage}
              </p>
            )}
          </div>
        )}

        {error && (
          <div
            id="sr-complete-response-error"
            ref={errorRef}
            role="alert"
            aria-live="assertive"
            tabIndex={-1}
            className="admin-notice admin-notice--error"
            style={{ marginTop: '12px', marginBottom: 0 }}
          >
            {error}
          </div>
        )}
      </div>
      <div
        className="modal-footer"
        style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', padding: '1rem' }}
      >
        <Button variant="secondary" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="success"
          onClick={handleConfirm}
          isLoading={isLoading}
          disabled={confirmDisabled}
        >
          Mark Complete
        </Button>
      </div>
    </Modal>
  );
};
